import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Input,
  Select,
  Divider,
  IconButton,
  Avatar,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Tooltip
} from '@chakra-ui/react';
import { MessageSquare, Clock, AlertCircle, CheckCircle } from 'lucide-react';

interface ContactSubmission {
  id: number;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  created_at: string;
  updated_at: string;
  assigned_to?: string;
  internal_notes?: string;
  response_count: number;
}

const Contacts: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedContact, setSelectedContact] = useState<ContactSubmission | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [newResponse, setNewResponse] = useState('');
  const [internalNote, setInternalNote] = useState('');

  // Mock data - in real app, this would come from API
  const contacts: ContactSubmission[] = [
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@techstart.com',
      phone: '+1 (555) 123-4567',
      subject: 'API Integration Issues',
      message: 'We are experiencing intermittent 500 errors when calling the user authentication endpoint. This started happening after the recent deployment.',
      status: 'in_progress',
      priority: 'high',
      created_at: '2024-01-15T10:30:00Z',
      updated_at: '2024-01-15T14:20:00Z',
      assigned_to: 'Billy Bryan',
      internal_notes: 'Checked logs - appears to be related to database connection pool. Investigating further.',
      response_count: 2
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'm.chen@digitalcorp.io',
      subject: 'Feature Request - Bulk Data Export',
      message: 'Could we add a feature to export user data in bulk? We need this for our quarterly reporting process.',
      status: 'new',
      priority: 'medium',
      created_at: '2024-01-14T16:45:00Z',
      updated_at: '2024-01-14T16:45:00Z',
      response_count: 0
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      email: 'emily@cloudvision.com',
      phone: '+1 (555) 987-6543',
      subject: 'Billing Question',
      message: 'I noticed a discrepancy in our latest invoice. The usage metrics dont match our internal tracking.',
      status: 'resolved',
      priority: 'low',
      created_at: '2024-01-12T09:15:00Z',
      updated_at: '2024-01-13T11:30:00Z',
      assigned_to: 'Alex Thompson',
      internal_notes: 'Invoice corrected and sent. Customer confirmed resolution.',
      response_count: 3
    },
    {
      id: 4,
      name: 'David Kim',
      email: 'dkim@innovatehub.co',
      subject: 'Service Outage Report',
      message: 'Our application experienced a complete outage from 2:00-2:45 PM EST today. Can you provide details about what happened?',
      status: 'in_progress',
      priority: 'urgent',
      created_at: '2024-01-15T19:30:00Z',
      updated_at: '2024-01-15T20:15:00Z',
      assigned_to: 'Sarah Williams',
      internal_notes: 'AWS issue confirmed. Preparing detailed incident report.',
      response_count: 1
    },
    {
      id: 5,
      name: 'Lisa Wong',
      email: 'lwong@devforward.net',
      subject: 'Account Access Issues',
      message: 'Multiple team members are unable to log in. Getting "Invalid credentials" error even with correct passwords.',
      status: 'closed',
      priority: 'high',
      created_at: '2024-01-10T08:20:00Z',
      updated_at: '2024-01-11T17:45:00Z',
      assigned_to: 'Billy Bryan',
      internal_notes: 'Session cache issue resolved. All users can now access their accounts.',
      response_count: 4
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'blue';
      case 'in_progress': return 'orange';
      case 'resolved': return 'green';
      case 'closed': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new': return AlertCircle;
      case 'in_progress': return Clock;
      case 'resolved': return CheckCircle;
      case 'closed': return CheckCircle;
      default: return AlertCircle;
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || contact.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const openContactDetail = (contact: ContactSubmission) => {
    setSelectedContact(contact);
    onOpen();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusStats = () => {
    const stats = {
      new: contacts.filter(c => c.status === 'new').length,
      in_progress: contacts.filter(c => c.status === 'in_progress').length,
      resolved: contacts.filter(c => c.status === 'resolved').length,
      closed: contacts.filter(c => c.status === 'closed').length
    };
    return stats;
  };

  const stats = getStatusStats();

  return (
    <Box>
      {/* Header */}
      <Box mb={8}>
        <Heading size="lg" mb={2} color="white">Contact Management</Heading>
        <Text color="gray.300">Manage customer inquiries, support tickets, and communications</Text>
      </Box>

      {/* Stats Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} gap={4} mb={8}>
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.400">New Requests</Text>
                <Text fontSize="2xl" fontWeight="bold" color="blue.400">{stats.new}</Text>
              </VStack>
              <Box p={2} bg="blue.900" borderRadius="lg" border="1px" borderColor="blue.700">
                <AlertCircle size={20} color="var(--chakra-colors-blue-400)" />
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.400">In Progress</Text>
                <Text fontSize="2xl" fontWeight="bold" color="orange.400">{stats.in_progress}</Text>
              </VStack>
              <Box p={2} bg="orange.900" borderRadius="lg" border="1px" borderColor="orange.700">
                <Clock size={20} color="var(--chakra-colors-orange-400)" />
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.400">Resolved</Text>
                <Text fontSize="2xl" fontWeight="bold" color="green.400">{stats.resolved}</Text>
              </VStack>
              <Box p={2} bg="green.900" borderRadius="lg" border="1px" borderColor="green.700">
                <CheckCircle size={20} color="var(--chakra-colors-green-400)" />
              </Box>
            </HStack>
          </CardBody>
        </Card>

        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.400">Closed</Text>
                <Text fontSize="2xl" fontWeight="bold" color="gray.400">{stats.closed}</Text>
              </VStack>
              <Box p={2} bg="gray.700" borderRadius="lg" border="1px" borderColor="gray.600">
                <CheckCircle size={20} color="var(--chakra-colors-gray-400)" />
              </Box>
            </HStack>
          </CardBody>
        </Card>
      </Grid>

      {/* Filters */}
      <Card bg="gray.800" borderColor="gray.700" mb={6}>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>Search</Text>
              <Input
                placeholder="Search contacts, emails, subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
              />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>Status</Text>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Select>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.400" mb={2}>Priority</Text>
              <Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
              >
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Select>
            </Box>
          </Grid>
        </CardBody>
      </Card>

      {/* Contact List */}
      <Card bg="gray.800" borderColor="gray.700">
        <CardBody>
          <VStack spacing={4} align="stretch">
            {filteredContacts.map((contact) => {
              const StatusIcon = getStatusIcon(contact.status);
              return (
                <Box key={contact.id}>
                  <HStack spacing={4} align="start">
                    <Avatar name={contact.name} size="sm" />
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack spacing={2}>
                        <Text fontWeight="medium" color="white">
                          {contact.name}
                        </Text>
                        <Badge colorScheme={getStatusColor(contact.status)} variant="subtle">
                          {contact.status.replace('_', ' ')}
                        </Badge>
                        <Badge colorScheme={getPriorityColor(contact.priority)} variant="outline">
                          {contact.priority}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" color="gray.400">{contact.email}</Text>
                      <Text fontWeight="medium" color="gray.200">{contact.subject}</Text>
                      <Text fontSize="sm" color="gray.400" noOfLines={2}>
                        {contact.message}
                      </Text>
                      <HStack spacing={4} fontSize="xs" color="gray.500">
                        <Text>Created: {formatDate(contact.created_at)}</Text>
                        <Text>Updated: {formatDate(contact.updated_at)}</Text>
                        {contact.response_count > 0 && (
                          <HStack spacing={1}>
                            <MessageSquare size={12} />
                            <Text>{contact.response_count} responses</Text>
                          </HStack>
                        )}
                      </HStack>
                    </VStack>
                    <VStack spacing={2}>
                      <Tooltip label={`Status: ${contact.status.replace('_', ' ')}`}>
                        <IconButton
                          aria-label="Status"
                          icon={<StatusIcon size={16} />}
                          size="sm"
                          variant="ghost"
                          colorScheme={getStatusColor(contact.status)}
                        />
                      </Tooltip>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => openContactDetail(contact)}
                      >
                        View Details
                      </Button>
                    </VStack>
                  </HStack>
                  <Divider mt={4} borderColor="gray.700" />
                </Box>
              );
            })}
          </VStack>
        </CardBody>
      </Card>

      {/* Contact Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">
            {selectedContact?.name} - {selectedContact?.subject}
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody pb={6}>
            {selectedContact && (
              <Tabs colorScheme="blue">
                <TabList>
                  <Tab color="gray.300" _selected={{ color: 'blue.400' }}>Details</Tab>
                  <Tab color="gray.300" _selected={{ color: 'blue.400' }}>Conversation</Tab>
                  <Tab color="gray.300" _selected={{ color: 'blue.400' }}>Internal Notes</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <Box>
                          <Text fontSize="sm" color="gray.400" mb={1}>Name</Text>
                          <Text color="white">{selectedContact.name}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400" mb={1}>Email</Text>
                          <Text color="white">{selectedContact.email}</Text>
                        </Box>
                        {selectedContact.phone && (
                          <Box>
                            <Text fontSize="sm" color="gray.400" mb={1}>Phone</Text>
                            <Text color="white">{selectedContact.phone}</Text>
                          </Box>
                        )}
                        <Box>
                          <Text fontSize="sm" color="gray.400" mb={1}>Status</Text>
                          <Badge colorScheme={getStatusColor(selectedContact.status)}>
                            {selectedContact.status.replace('_', ' ')}
                          </Badge>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.400" mb={1}>Priority</Text>
                          <Badge colorScheme={getPriorityColor(selectedContact.priority)}>
                            {selectedContact.priority}
                          </Badge>
                        </Box>
                        {selectedContact.assigned_to && (
                          <Box>
                            <Text fontSize="sm" color="gray.400" mb={1}>Assigned To</Text>
                            <Text color="white">{selectedContact.assigned_to}</Text>
                          </Box>
                        )}
                      </Grid>
                      
                      <Divider borderColor="gray.700" />
                      
                      <Box>
                        <Text fontSize="sm" color="gray.400" mb={2}>Original Message</Text>
                        <Box bg="gray.900" p={4} borderRadius="lg" border="1px" borderColor="gray.700">
                          <Text color="gray.200">{selectedContact.message}</Text>
                        </Box>
                      </Box>
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text fontSize="sm" color="gray.400" mb={2}>Conversation History</Text>
                        <Box bg="gray.900" p={4} borderRadius="lg" border="1px" borderColor="gray.700" minH="200px">
                          <Text color="gray.400" fontSize="sm">
                            Conversation history would be loaded here from the backend API
                          </Text>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" color="gray.400" mb={2}>Send Response</Text>
                        <Textarea
                          value={newResponse}
                          onChange={(e) => setNewResponse(e.target.value)}
                          placeholder="Type your response here..."
                          bg="gray.900"
                          borderColor="gray.600"
                          color="white"
                          rows={4}
                        />
                        <HStack mt={2} justify="flex-end">
                          <Button size="sm" variant="outline" colorScheme="gray">
                            Save as Draft
                          </Button>
                          <Button size="sm" colorScheme="blue">
                            Send Response
                          </Button>
                        </HStack>
                      </Box>
                    </VStack>
                  </TabPanel>

                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text fontSize="sm" color="gray.400" mb={2}>Internal Notes</Text>
                        <Box bg="gray.900" p={4} borderRadius="lg" border="1px" borderColor="gray.700">
                          <Text color="gray.200">
                            {selectedContact.internal_notes || 'No internal notes yet.'}
                          </Text>
                        </Box>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" color="gray.400" mb={2}>Add Internal Note</Text>
                        <Textarea
                          value={internalNote}
                          onChange={(e) => setInternalNote(e.target.value)}
                          placeholder="Add internal note (not visible to customer)..."
                          bg="gray.900"
                          borderColor="gray.600"
                          color="white"
                          rows={3}
                        />
                        <HStack mt={2} justify="flex-end">
                          <Button size="sm" colorScheme="green">
                            Add Note
                          </Button>
                        </HStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Contacts;